import { Check, ChevronDown, Download, LogOut } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUserOptional } from "@/auth/UserContext";
import { TWIDO_APP_NAME } from "@/branding";
import {
  organizationLabel,
  useOrganization,
} from "@/context/OrganizationContext";
import { usePreferencesUI } from "@/context/PreferencesUIContext";
import { usePwaInstall } from "@/context/PwaInstallContext";
import {
  SensitiveIdentity,
  usePrivacyMasking,
} from "@/components/common/Sensitive";
import { formatConsigneeNavLabel } from "@/lib/clientPortalDisplay";
import { useClientConsigneeDisplayLabel } from "@/hooks/useClientConsigneeDisplayLabel";
import { initials, resolveDisplayName, roleLabel } from "@/lib/userDisplay";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserAccountMenuProps {
  /** Full label on desktop; icon-only avatar on mobile. */
  variant?: "desktop" | "mobile";
  className?: string;
}

/** Account menu — preferences, admin, org switch (mobile), sign out. */
export function UserAccountMenu({
  variant = "desktop",
  className,
}: UserAccountMenuProps) {
  const navigate = useNavigate();
  const { openPreferences } = usePreferencesUI();
  const {
    canShowInstallItem,
    canPromptInstall,
    promptInstall,
    openInstallInstructions,
  } = usePwaInstall();
  const userCtx = useUserOptional();
  const {
    organization,
    organizations,
    currentClientKey,
    canSwitch,
    switchOrganization,
  } = useOrganization();

  const displayName = resolveDisplayName(
    userCtx?.profile?.name ?? userCtx?.user?.name,
    userCtx?.profile?.email ?? userCtx?.user?.email,
  );
  const consigneeLink = userCtx?.profile?.consignee_link;
  const consigneeEntityLabel = useClientConsigneeDisplayLabel(
    consigneeLink,
    Boolean(userCtx?.isClient && consigneeLink?.trim()),
  );
  const accountTriggerLabel =
    userCtx?.isClient && consigneeLink?.trim()
      ? formatConsigneeNavLabel(consigneeLink, consigneeEntityLabel)
      : displayName;
  const displayRole = roleLabel(userCtx?.profile?.role);
  const avatarUrl = userCtx?.user?.picture ?? undefined;
  const userInitials = initials(displayName);
  const showOrgSwitch = variant === "mobile" && canSwitch;
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const hide = usePrivacyMasking();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "desktop" ? (
            <Button
              variant="ghost"
              className={cn(
                "h-9 gap-2 rounded-full px-2 hover:bg-accent",
                className,
              )}
              aria-label={hide ? "Account" : undefined}
            >
              <Avatar className="size-7">
                {avatarUrl && (
                  <AvatarImage src={avatarUrl} alt={displayName} />
                )}
                <AvatarFallback className="bg-primary/20 text-primary">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <SensitiveIdentity
                value={accountTriggerLabel}
                className="max-w-[10rem] truncate text-sm"
              />
              <ChevronDown className="size-4 opacity-60" />
            </Button>
          ) : (
            <Button
              variant="shell"
              size="touchIcon"
              className={cn("shrink-0", className)}
              aria-label="Account"
            >
              <Avatar className="size-7">
                {avatarUrl && (
                  <AvatarImage src={avatarUrl} alt={displayName} />
                )}
                <AvatarFallback className="bg-primary/20 text-primary">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          side="bottom"
          sideOffset={14}
          className="w-56"
        >
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <SensitiveIdentity value={displayName} className="truncate" />
            {variant === "mobile" && (
              <SensitiveIdentity
                value={organization}
                className="truncate text-xs font-normal text-muted-foreground"
              />
            )}
            {displayRole && (
              <span className="text-xs font-normal text-muted-foreground">
                {displayRole}
              </span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showOrgSwitch && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Switch organization
              </DropdownMenuLabel>
              {organizations.map((org) => {
                const label = organizationLabel(org);
                const isCurrent = org.client_key === currentClientKey;
                return (
                  <DropdownMenuItem
                    key={org.auth0_org_id || org.client_key}
                    disabled={isCurrent || !org.auth0_org_id}
                    onClick={() => {
                      if (isCurrent || !org.auth0_org_id) return;
                      switchOrganization(org.auth0_org_id);
                    }}
                    className="justify-between gap-2"
                  >
                    <SensitiveIdentity value={label} className="truncate" />
                    {isCurrent && (
                      <Check className="size-4 shrink-0 text-primary" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
            </>
          )}
          {userCtx?.isAdmin && (
            <DropdownMenuItem onClick={() => navigate("/admin")}>
              Administration
            </DropdownMenuItem>
          )}
          {canShowInstallItem && (
            <DropdownMenuItem
              onClick={() =>
                canPromptInstall ? promptInstall() : openInstallInstructions()
              }
            >
              <Download />
              Install app
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={openPreferences}>
            Preferences
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              requestAnimationFrame(() => setConfirmSignOut(true));
            }}
          >
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirmSignOut} onOpenChange={setConfirmSignOut}>
        <DialogContent className="glass-overlay border-border/60 p-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="size-4" />
              Sign out?
            </DialogTitle>
            <DialogDescription>
              You’ll need to sign in again to access {TWIDO_APP_NAME}.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmSignOut(false)}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => userCtx?.logout()}>
              Sign out
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
